import { useEffect, useMemo, useState } from "react";
import { api, apiErrToStr, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { normalizedRole } from "@/lib/permissions";
import { Plus, Trash2, X, Save, Loader2, Pencil, Ban, CheckCircle2 } from "lucide-react";

const emptyForm = {
  email: "",
  password: "",
  name: "",
  role: "callcenter",
  tenant_id: "default",
  enabled: true,
  permissions: [],
  webphone_extension_id: "",
  rbac_role: "CALLCENTER_AGENT",
  assigned_queue_ids: [],
  permission_overrides: [],
  permission_denied: [],
  permission_scopes: {
    reports: "assigned_queues",
    cdr: "assigned_queues",
    recordings: "assigned_queues",
  },
};

function roleCatalogKey(role) {
  const normalized = normalizedRole(role);
  if (normalized === "tenant_admin") return "tenant_admin";
  if (normalized === "manager") return "manager";
  if (normalized === "operator") return "operator";
  return normalized || "operator";
}

const rbacRoleOptions = [
  { value: "CLIENT_ADMIN", label: "Admin Cliente" },
  { value: "CALLCENTER_SUPERVISOR", label: "Supervisor Call Center" },
  { value: "CALLCENTER_AGENT", label: "Agente de Call Center" },
  { value: "EXTENSION_USER", label: "Usuario de Ramal" },
  { value: "CUSTOM", label: "Personalizado" },
];

const rbacRoleToLegacyRole = {
  SUPER_ADMIN: "superadmin",
  CLIENT_ADMIN: "admin",
  CALLCENTER_SUPERVISOR: "supervisor",
  CALLCENTER_AGENT: "callcenter",
  EXTENSION_USER: "viewer",
};

const scopeOptions = [
  { value: "own", label: "Somente proprio usuario" },
  { value: "extension", label: "Ramal vinculado" },
  { value: "assigned_queues", label: "Filas permitidas" },
  { value: "tenant", label: "Todo o cliente" },
  { value: "all_tenants", label: "Todos os clientes" },
];

function normalizeArray(value) {
  return Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : [];
}

function PermissionGroup({ group, options, selectedPermissions, onToggleOne, onToggleGroup }) {
  const keys = options.map((item) => item.key);
  const selectedCount = keys.filter((key) => selectedPermissions.has(key)).length;
  const allSelected = keys.length > 0 && selectedCount === keys.length;

  return (
    <div className="border border-[color:var(--border-default)] rounded-sm p-3 bg-white/60">
      <label className="flex items-center justify-between gap-3 text-sm font-medium mb-3">
        <span className="flex items-center gap-2">
          <input
            type="checkbox"
            ref={(node) => {
              if (node) node.indeterminate = selectedCount > 0 && selectedCount < keys.length;
            }}
            checked={allSelected}
            onChange={() => onToggleGroup(keys, !allSelected)}
          />
          {group.label}
        </span>
        <span className="text-[11px] font-mono text-[color:var(--text-muted)]">{selectedCount}/{keys.length}</span>
      </label>
      <div className="space-y-2">
        {options.map((item) => (
          <label key={item.key} className="flex items-start gap-2 text-sm text-[color:var(--text-secondary)]">
            <input
              type="checkbox"
              checked={selectedPermissions.has(item.key)}
              onChange={() => onToggleOne(item.key)}
            />
            <span>{item.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function Users() {
  const { user: current, activeTenantId } = useAuth();
  const currentRole = normalizedRole(current);
  const isAdmin = ["tenant_admin", "superadmin"].includes(currentRole);
  const isSuperadmin = currentRole === "superadmin";

  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [extensions, setExtensions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [permissionCatalog, setPermissionCatalog] = useState({ groups: [], role_defaults: {} });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [actionId, setActionId] = useState("");
  const [error, setError] = useState("");

  const preferredTenantId = useMemo(() => {
    if (isSuperadmin) return activeTenantId || current?.tenant_id || "default";
    return current?.tenant_id || activeTenantId || "default";
  }, [activeTenantId, current?.tenant_id, isSuperadmin]);

  const roleLabel = (role) => {
    const value = String(role || "").toLowerCase();
    if (["admin", "tenant_admin", "superadmin"].includes(value)) return "admin de empresa";
    if (["manager", "supervisor"].includes(value)) return "supervisor";
    if (["callcenter", "operator"].includes(value)) return "agente";
    return "usuario";
  };

  const resetForm = () => {
    setEditingId("");
    setForm({
      ...emptyForm,
      tenant_id: preferredTenantId,
    });
    setError("");
  };

  const load = async () => {
    setLoading(true);
    try {
      const requests = [api.get("/users"), api.get("/users/permissions/catalog"), api.get("/filas")];
      if (isSuperadmin) requests.push(api.get("/tenants"));
      const [usersRes, permissionsRes, queuesRes, tenantsRes] = await Promise.all(requests);
      setRows(usersRes.data);
      setPermissionCatalog(permissionsRes.data || { groups: [], role_defaults: {} });
      setQueues(Array.isArray(queuesRes?.data) ? queuesRes.data : []);
      const tenantRows = tenantsRes?.data || [];
      setTenants(tenantRows);
      if (isSuperadmin && !editingId) {
        setForm((currentForm) => {
          const tenantIds = new Set((tenantRows || []).map((tenant) => String(tenant.id || "").trim()).filter(Boolean));
          if (currentForm.tenant_id && tenantIds.has(String(currentForm.tenant_id))) return currentForm;
          const preferredTenant =
            tenantRows.find((tenant) => String(tenant.id) === String(activeTenantId || "")) ||
            tenantRows.find((tenant) => String(tenant.id) === String(current?.tenant_id || "")) ||
            tenantRows.find((tenant) => String(tenant.id) !== "default") ||
            tenantRows[0];
          return {
            ...currentForm,
            tenant_id: preferredTenant?.id || currentForm.tenant_id || "default",
          };
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    resetForm();
    load();
  }, [isSuperadmin, preferredTenantId]);

  useEffect(() => {
    const tenantId = String(isSuperadmin ? (form.tenant_id || preferredTenantId) : preferredTenantId).trim();
    if (!tenantId) {
      setExtensions([]);
      return;
    }
    let cancelled = false;
    const loadExtensions = async () => {
      try {
        const config = isSuperadmin ? { skipTenantScope: true } : undefined;
        const { data } = await api.get("/ramais", config);
        if (!cancelled) setExtensions(Array.isArray(data) ? data : []);
      } catch (_err) {
        if (!cancelled) setExtensions([]);
      }
    };
    loadExtensions();
    return () => {
      cancelled = true;
    };
  }, [form.tenant_id, current?.tenant_id, activeTenantId, isSuperadmin]);

  const selectedTenant = useMemo(() => {
    const selectedId = String(form.tenant_id || preferredTenantId || "").trim();
    return (tenants || []).find((tenant) => String(tenant.id || "").trim() === selectedId) || null;
  }, [form.tenant_id, preferredTenantId, tenants]);

  const webphoneTenantPath = useMemo(() => {
    return (
      selectedTenant?.slug ||
      selectedTenant?.id ||
      current?.tenant?.slug ||
      preferredTenantId ||
      "cliente"
    );
  }, [current?.tenant?.slug, preferredTenantId, selectedTenant]);

  const tenantNameById = useMemo(
    () => Object.fromEntries((tenants || []).map((tenant) => [tenant.id, tenant.nome || tenant.id])),
    [tenants],
  );

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "pt-BR")),
    [rows],
  );

  const openCreate = () => {
    resetForm();
    setForm((currentForm) => ({
      ...currentForm,
      permissions: [...(permissionCatalog.role_defaults?.[roleCatalogKey(currentForm.role)] || [])],
    }));
    setShowForm(true);
  };

  const openEdit = (user) => {
    setEditingId(user.id);
    setForm({
      email: user.email || "",
      password: "",
      name: user.name || "",
      role: user.role || "operator",
      tenant_id: user.tenant_id || current?.tenant_id || "default",
      enabled: user.enabled !== false,
      permissions: Array.isArray(user.permissions)
        ? [...user.permissions]
        : [...(permissionCatalog.role_defaults?.[roleCatalogKey(user.role || "operator")] || [])],
      webphone_extension_id: user.webphone_extension_id || "",
      rbac_role: user.rbac_role || "CUSTOM",
      assigned_queue_ids: user.assigned_queue_ids || [],
      permission_overrides: user.permission_overrides || [],
      permission_denied: user.permission_denied || [],
      permission_scopes: {
        ...emptyForm.permission_scopes,
        ...(user.permission_scopes || {}),
      },
    });
    setError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        email: form.email,
        name: form.name,
        role: form.role,
        enabled: form.enabled,
        permissions: form.permissions,
        webphone_extension_id: form.webphone_extension_id || "",
        rbac_role: form.rbac_role,
        assigned_queue_ids: form.assigned_queue_ids,
        permission_overrides: form.permission_overrides,
        permission_denied: form.permission_denied,
        permission_scopes: form.permission_scopes,
      };
      if (isSuperadmin) payload.tenant_id = form.tenant_id;
      if (form.password) payload.password = form.password;

      if (editingId) {
        await api.put(`/users/${editingId}`, payload);
      } else {
        await api.post("/users", payload);
      }
      closeForm();
      await load();
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (user) => {
    const nextEnabled = user.enabled === false;
    const actionLabel = nextEnabled ? "reativar" : "suspender";
    if (!window.confirm(`Deseja ${actionLabel} este usuario?`)) return;
    setActionId(user.id);
    try {
      await api.put(`/users/${user.id}`, { enabled: nextEnabled });
      await load();
    } catch (err) {
      alert(apiErrToStr(err?.response?.data?.detail));
    } finally {
      setActionId("");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir usuario?")) return;
    setActionId(id);
    try {
      await api.delete(`/users/${id}`);
      await load();
    } catch (err) {
      alert(apiErrToStr(err?.response?.data?.detail));
    } finally {
      setActionId("");
    }
  };

  const roleTag = (role) => {
    const value = String(role || "").toLowerCase();
    if (["admin", "tenant_admin", "superadmin"].includes(value)) return <span className="sbc-tag tag-danger">admin empresa</span>;
    if (["manager", "supervisor"].includes(value)) return <span className="sbc-tag tag-online">supervisor</span>;
    if (["callcenter", "operator"].includes(value)) return <span className="sbc-tag tag-info">agente</span>;
    return <span className="sbc-tag tag-offline">usuario</span>;
  };

  const rbacRoleLabel = (role) => {
    if (role === "SUPER_ADMIN") return "Super Admin";
    return rbacRoleOptions.find((item) => item.value === role)?.label || "Personalizado";
  };

  const statusTag = (enabled) => (
    enabled === false
      ? <span className="sbc-tag tag-danger">suspenso</span>
      : <span className="sbc-tag tag-online">ativo</span>
  );

  const togglePermission = (permissionKey) => {
    setForm((currentForm) => {
      const currentPermissions = new Set(currentForm.permissions || []);
      if (currentPermissions.has(permissionKey)) currentPermissions.delete(permissionKey);
      else currentPermissions.add(permissionKey);
      return { ...currentForm, permissions: [...currentPermissions].sort() };
    });
  };

  const togglePermissionGroup = (permissionKeys, shouldEnable) => {
    setForm((currentForm) => {
      const currentPermissions = new Set(currentForm.permissions || []);
      permissionKeys.forEach((key) => {
        if (shouldEnable) currentPermissions.add(key);
        else currentPermissions.delete(key);
      });
      return { ...currentForm, permissions: [...currentPermissions].sort() };
    });
  };

  const applyRbacRole = (rbacRole) => {
    const legacyRole = rbacRoleToLegacyRole[rbacRole] || form.role || "viewer";
    const defaults = permissionCatalog.role_defaults?.[roleCatalogKey(legacyRole)] || [];
    setForm((currentForm) => {
      const nextPermissions = new Set(defaults);
      if ((currentForm.permissions || []).includes("webphone.use")) nextPermissions.add("webphone.use");
      return {
        ...currentForm,
        rbac_role: rbacRole,
        role: legacyRole,
        permissions: rbacRole === "CUSTOM" ? currentForm.permissions : [...nextPermissions].sort(),
        assigned_queue_ids: ["CALLCENTER_SUPERVISOR", "CALLCENTER_AGENT"].includes(rbacRole)
          ? normalizeArray(currentForm.assigned_queue_ids)
          : [],
        permission_scopes: {
          ...emptyForm.permission_scopes,
          ...(currentForm.permission_scopes || {}),
          reports: rbacRole === "CLIENT_ADMIN" ? "tenant" : (currentForm.permission_scopes?.reports || "assigned_queues"),
          cdr: rbacRole === "CLIENT_ADMIN" ? "tenant" : (currentForm.permission_scopes?.cdr || "assigned_queues"),
          recordings: rbacRole === "CLIENT_ADMIN" ? "tenant" : (currentForm.permission_scopes?.recordings || "assigned_queues"),
        },
      };
    });
  };

  const toggleAssignedQueue = (queueId) => {
    setForm((currentForm) => {
      const currentQueues = new Set(normalizeArray(currentForm.assigned_queue_ids));
      if (currentQueues.has(queueId)) currentQueues.delete(queueId);
      else currentQueues.add(queueId);
      return { ...currentForm, assigned_queue_ids: [...currentQueues].sort() };
    });
  };

  const updateScope = (scopeKey, value) => {
    setForm((currentForm) => ({
      ...currentForm,
      permission_scopes: {
        ...(currentForm.permission_scopes || {}),
        [scopeKey]: value,
      },
    }));
  };

  const applyRoleDefaults = (role) => {
    const defaults = permissionCatalog.role_defaults?.[roleCatalogKey(role)] || [];
    setForm((currentForm) => {
      const nextPermissions = new Set(defaults);
      if ((currentForm.permissions || []).includes("webphone.use")) nextPermissions.add("webphone.use");
      return { ...currentForm, role, permissions: [...nextPermissions].sort() };
    });
  };

  const webphoneEnabled = (form.permissions || []).includes("webphone.use");

  const availableExtensions = useMemo(() => {
    const tenantId = String(isSuperadmin ? (form.tenant_id || preferredTenantId) : preferredTenantId).trim();
    return (extensions || [])
      .filter((row) => !tenantId || String(row?.tenant_id || "").trim() === tenantId)
      .sort((a, b) => String(a.numero || "").localeCompare(String(b.numero || ""), "pt-BR"));
  }, [extensions, form.tenant_id, isSuperadmin, preferredTenantId]);

  const availableQueues = useMemo(() => {
    const tenantId = String(isSuperadmin ? (form.tenant_id || preferredTenantId) : preferredTenantId).trim();
    return (queues || [])
      .filter((row) => !tenantId || String(row?.tenant_id || "").trim() === tenantId)
      .sort((a, b) => String(a.numero || a.nome || "").localeCompare(String(b.numero || b.nome || ""), "pt-BR"));
  }, [queues, form.tenant_id, isSuperadmin, preferredTenantId]);

  const selectedPermissions = useMemo(() => new Set(form.permissions || []), [form.permissions]);
  const selectedQueues = useMemo(() => new Set(normalizeArray(form.assigned_queue_ids)), [form.assigned_queue_ids]);
  const showQueueScope = ["CALLCENTER_SUPERVISOR", "CALLCENTER_AGENT"].includes(form.rbac_role);

  useEffect(() => {
    if (!form.webphone_extension_id) return;
    if (availableExtensions.some((row) => row.id === form.webphone_extension_id)) return;
    setForm((currentForm) => ({ ...currentForm, webphone_extension_id: "" }));
  }, [availableExtensions, form.webphone_extension_id]);

  const toggleWebphonePermission = (enabled) => {
    setForm((currentForm) => {
      const nextPermissions = new Set(currentForm.permissions || []);
      if (enabled) nextPermissions.add("webphone.use");
      else nextPermissions.delete("webphone.use");
      return {
        ...currentForm,
        permissions: [...nextPermissions].sort(),
        webphone_extension_id: enabled ? currentForm.webphone_extension_id : "",
      };
    });
  };

  return (
    <div className="space-y-5" data-testid="usuarios-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Access control</div>
          <h1 className="text-2xl font-semibold tracking-tight">Usuarios</h1>
        </div>
        {isAdmin && (
          <button className="sbc-btn sbc-btn-primary" onClick={openCreate} data-testid="usuarios-add-btn">
            <Plus size={14} /> Novo usuario
          </button>
        )}
      </div>

      <div className="sbc-card overflow-hidden">
        <table className="sbc-table" data-testid="usuarios-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Cliente</th>
              <th>Role</th>
              <th>Modulos</th>
              <th>Status</th>
              <th>Criado em</th>
              <th className="text-right">Acoes</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center text-[color:var(--text-muted)] py-8">Carregando...</td></tr>
            ) : sortedRows.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-[color:var(--text-muted)] py-8">Nenhum usuario cadastrado.</td></tr>
            ) : sortedRows.map((u) => (
              <tr key={u.id}>
                <td className="font-medium">{u.name}</td>
                <td className="font-mono">{u.email}</td>
                <td className="font-mono text-xs">
                  <div>{tenantNameById[u.tenant_id] || u.tenant_id || "default"}</div>
                  <div className="text-[10px] text-[color:var(--text-muted)]">{u.tenant_id || "default"}</div>
                </td>
                <td>
                  <div>{roleTag(u.role)}</div>
                  <div className="mt-1 text-[10px] text-[color:var(--text-muted)]">{rbacRoleLabel(u.rbac_role)}</div>
                </td>
                <td className="font-mono text-xs text-[color:var(--text-secondary)]">
                  <div>{Array.isArray(u.permissions) ? `${u.permissions.length} permissoes` : "padrao"}</div>
                  {Array.isArray(u.assigned_queue_ids) && u.assigned_queue_ids.length ? (
                    <div className="mt-1 text-[10px]">{u.assigned_queue_ids.length} fila(s)</div>
                  ) : null}
                </td>
                <td>{statusTag(u.enabled)}</td>
                <td className="font-mono text-xs text-[color:var(--text-secondary)]">{fmtDate(u.created_at)}</td>
                <td className="text-right">
                  {isAdmin && (
                    <div className="inline-flex items-center gap-2 flex-wrap justify-end">
                      <button
                        className="sbc-btn"
                        onClick={() => openEdit(u)}
                        data-testid={`usuarios-edit-${u.id}`}
                        title="Editar usuario"
                      >
                        <Pencil size={12} /> Editar
                      </button>
                      {u.id !== current?.id && (
                        <button
                          className={`sbc-btn ${u.enabled === false ? "sbc-btn-primary" : "sbc-btn-danger"}`}
                          onClick={() => toggleEnabled(u)}
                          disabled={actionId === u.id}
                          data-testid={`usuarios-toggle-${u.id}`}
                          title={u.enabled === false ? "Reativar usuario" : "Suspender usuario"}
                        >
                          {actionId === u.id ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : u.enabled === false ? (
                            <CheckCircle2 size={12} />
                          ) : (
                            <Ban size={12} />
                          )}
                        </button>
                      )}
                      {u.id !== current?.id && (
                        <button
                          className="sbc-btn sbc-btn-danger"
                          onClick={() => del(u.id)}
                          disabled={actionId === u.id}
                          data-testid={`usuarios-delete-${u.id}`}
                          title="Excluir usuario"
                        >
                          {actionId === u.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-40 flex items-center justify-center p-4" onClick={closeForm}>
          <div className="sbc-card w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">{editingId ? "Editar usuario" : "Novo usuario"}</h2>
              <button className="sbc-btn" onClick={closeForm}><X size={14} /></button>
            </div>
            <form onSubmit={save} className="space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] block mb-1">Nome</label>
                  <input
                    className="sbc-input"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="usuarios-field-name"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] block mb-1">Email</label>
                  <input
                    className="sbc-input font-mono"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="usuarios-field-email"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] block mb-1">
                    Senha {editingId ? "(deixe em branco para manter)" : ""}
                  </label>
                  <input
                    className="sbc-input font-mono"
                    required={!editingId}
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="usuarios-field-password"
                  />
                </div>
                {isSuperadmin && (
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] block mb-1">Cliente</label>
                    <select
                      className="sbc-input"
                      value={form.tenant_id}
                      onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
                    >
                      {(tenants || []).map((tenant) => (
                        <option key={tenant.id} value={tenant.id}>{tenant.nome}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="border border-[color:var(--border-default)] rounded-sm p-4 space-y-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] block mb-1">Perfil de acesso</label>
                    <select
                      className="sbc-input"
                      value={form.rbac_role}
                      onChange={(e) => applyRbacRole(e.target.value)}
                    >
                      {rbacRoleOptions.map((item) => (
                        <option key={item.value} value={item.value}>{item.label}</option>
                      ))}
                      {isSuperadmin && <option value="SUPER_ADMIN">Super Admin</option>}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] block mb-1">Perfil legado</label>
                    <div className="flex flex-col gap-2 md:flex-row md:items-center">
                      <select
                        className="sbc-input"
                        value={form.role}
                        onChange={(e) => applyRoleDefaults(e.target.value)}
                        data-testid="usuarios-field-role"
                      >
                        <option value="admin">Admin de Empresa</option>
                        <option value="viewer">Usuario</option>
                        <option value="supervisor">Supervisor</option>
                        <option value="callcenter">Agente</option>
                        {isSuperadmin && <option value="superadmin">Super Admin</option>}
                      </select>
                      <button type="button" className="sbc-btn" onClick={() => applyRoleDefaults(form.role)}>
                        Padrao
                      </button>
                    </div>
                    <div className="mt-1 text-[11px] text-[color:var(--text-muted)]">
                      {roleLabel(form.role)}
                    </div>
                  </div>
                </div>
                {showQueueScope && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Filas permitidas</div>
                    {availableQueues.length ? (
                      <div className="grid gap-2 md:grid-cols-3">
                        {availableQueues.map((queue) => {
                          const queueId = String(queue.id || queue.numero || queue.nome);
                          return (
                            <label key={queueId} className="flex items-center gap-2 text-sm border border-[color:var(--border-default)] rounded-sm px-3 py-2">
                              <input
                                type="checkbox"
                                checked={selectedQueues.has(queueId)}
                                onChange={() => toggleAssignedQueue(queueId)}
                              />
                              <span className="truncate">{queue.numero || queue.nome || queueId}</span>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-xs text-[color:var(--text-muted)]">Nenhuma fila encontrada para este cliente.</div>
                    )}
                  </div>
                )}
                <div className="grid gap-3 md:grid-cols-3">
                  {[
                    ["reports", "Relatorios"],
                    ["cdr", "CDR"],
                    ["recordings", "Gravacoes"],
                  ].map(([scopeKey, label]) => (
                    <div key={scopeKey}>
                      <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] block mb-1">{label}</label>
                      <select
                        className="sbc-input"
                        value={form.permission_scopes?.[scopeKey] || "assigned_queues"}
                        onChange={(e) => updateScope(scopeKey, e.target.value)}
                      >
                        {scopeOptions
                          .filter((item) => isSuperadmin || item.value !== "all_tenants")
                          .map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                          ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border border-[color:var(--border-default)] rounded-sm p-4 space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">WebRTC / Ramal web</div>
                  <div className="text-xs text-[color:var(--text-secondary)] mt-1">
                    Vincule este usuario a um ramal para ele acessar o webphone em <span className="font-mono">/{webphoneTenantPath}/ramal-web</span> usando o mesmo usuario e senha do painel.
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-[color:var(--text-secondary)]">
                  <input
                    type="checkbox"
                    checked={webphoneEnabled}
                    onChange={(e) => toggleWebphonePermission(e.target.checked)}
                  />
                  Permitir uso do WebRTC neste usuario
                </label>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] block mb-1">Ramal vinculado</label>
                  <select
                    className="sbc-input"
                    value={form.webphone_extension_id}
                    onChange={(e) => setForm({ ...form, webphone_extension_id: e.target.value })}
                    disabled={!webphoneEnabled}
                  >
                    <option value="">-- selecione --</option>
                    {availableExtensions.map((row) => (
                      <option key={row.id} value={row.id}>
                        {row.numero} - {row.nome}{row.branch_name ? ` (${row.branch_name})` : ""}
                      </option>
                    ))}
                  </select>
                  {!availableExtensions.length ? (
                    <div className="mt-1 text-[11px] text-[color:var(--accent-red)]">
                      Nenhum ramal encontrado para este cliente no contexto atual.
                    </div>
                  ) : null}
                  <div className="mt-1 text-[11px] text-[color:var(--text-muted)]">
                    O login do webphone usa as credenciais deste usuario e registra no ramal selecionado.
                  </div>
                </div>
              </div>
              <div className="border border-[color:var(--border-default)] rounded-sm p-4 space-y-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Permissoes por modulo</div>
                  <div className="text-xs text-[color:var(--text-secondary)] mt-1">
                    Marque quais areas este usuario pode acessar. Isso controla o menu e o acesso do sistema.
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {(permissionCatalog.groups || []).map((group) => {
                    const options = (group.permissions || []).filter((item) => !(group.superadmin_only && !isSuperadmin));
                    if (!options.length) return null;
                    return (
                      <PermissionGroup
                        key={group.id}
                        group={group}
                        options={options}
                        selectedPermissions={selectedPermissions}
                        onToggleOne={togglePermission}
                        onToggleGroup={togglePermissionGroup}
                      />
                    );
                  })}
                </div>
              </div>
              {editingId && (
                <label className="flex items-center gap-2 text-sm text-[color:var(--text-secondary)]">
                  <input
                    type="checkbox"
                    checked={!!form.enabled}
                    onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  />
                  Usuario ativo
                </label>
              )}
              {error && (
                <div className="text-xs text-[color:var(--accent-red)] font-mono border border-[rgba(239,68,68,0.3)] bg-[rgba(239,68,68,0.08)] px-3 py-2 rounded-sm">
                  ! {error}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className="sbc-btn" onClick={closeForm}>Cancelar</button>
                <button type="submit" className="sbc-btn sbc-btn-primary" disabled={saving} data-testid="usuarios-form-save">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {editingId ? "Salvar" : "Criar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
